import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import {
  maskedEmail,
  OtpInputComponent,
  OtpSetting,
  OtpValueChangeType,
  REQUESTED_NEW_OTP,
} from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, map, Observable, of } from 'rxjs';

@Component({
  selector: 'app-verify-account',
  imports: [OtpInputComponent, TranslatePipe],
  templateUrl: './verify-account.component.html',
  styleUrl: './verify-account.component.scss',
})
export class VerifyAccountComponent {
  // UI state flags
  isVerificationError = signal(false);

  isSubmittingOtp = signal(false);

  isOtpSentSuccessfully = signal(false);

  public successMessage = input.required<string>();

  public defaultErrorMessage = input<string | false>(false);

  public validationErrorMessage = input<string | false>(false);

  public submitOtpAction =
    input.required<(signupRequestId: string, otp: string, email: string) => Observable<boolean>>();

  public resendOtpAction = input.required<(email: string) => Observable<boolean>>();

  public errorMessage = computed<string | false>(() => {
    const validationErrorMessage = this.validationErrorMessage();
    const defaultErrorMessage = this.defaultErrorMessage();

    if (validationErrorMessage || defaultErrorMessage) {
      return validationErrorMessage || defaultErrorMessage;
    }

    return false;
  });

  public otpSubmitted = output<{ otp: string; email: string }>();

  // OTP verification data
  enteredOtpCode = signal('');

  // Reference to OTP input component
  otpInputComponent = viewChild(OtpInputComponent);

  // OTP input configuration
  public otpInputSettings = signal<OtpSetting>({
    length: 6,
    timer: 30,
    timerType: 0,
    btnText: 'authentication.resend_code',
    btnLabel: 'authentication.did_not_receive_code',
    numbersOnly: true,
  });

  public signupRequestInfo = input.required<{ email: string; signupRequestId: string }>();

  public maskedEmailForDisplay = computed(() => {
    return maskedEmail(this.signupRequestInfo().email);
  });

  /**
   * Handles OTP input changes, timer completion, and resend requests.
   * @param inputValue - OTP string value, -1 for timer completion, -2 for resend request
   */
  onOtpInputChange(inputValue: OtpValueChangeType): void {
    if (typeof inputValue === 'string') {
      // Only clear error when user is typing (non-empty value)
      // Empty values are from resets, which should preserve error state
      if (inputValue.length > 0) {
        this.isVerificationError.set(false);
      }

      this.enteredOtpCode.set(inputValue);
    }

    if (inputValue === REQUESTED_NEW_OTP) {
      this.isVerificationError.set(false);
      this.resendOtp();
    }
  }

  /**
   * Triggers action to resend OTP
   */
  private resendOtp(): void {
    const resendOtpAction = this.resendOtpAction();

    resendOtpAction(this.signupRequestInfo().email).subscribe();
  }

  /**
   * Validates and submits OTP verification to backend.
   * Handles loading states and error responses.
   */
  submitOtp(): Observable<boolean> {
    const submitOtpAction = this.submitOtpAction();

    if (this.isSubmittingOtp()) {
      return of(false);
    }

    this.isSubmittingOtp.set(true);

    return submitOtpAction(
      this.signupRequestInfo().signupRequestId,
      this.enteredOtpCode(),
      this.signupRequestInfo().email
    ).pipe(
      finalize(() => this.isSubmittingOtp.set(false)),
      map(() => {
        this.otpSubmitted.emit({
          email: this.signupRequestInfo().email,
          otp: this.enteredOtpCode(),
        });
        return true;
      }),
      catchError(() => {
        this.isVerificationError.set(true);
        this.enteredOtpCode.set('');
        this.otpInputComponent()?.resetOtp();
        return of(false);
      })
    );
  }
}
